import { useContext } from "react";

import { ApiBulkExport, BulkExportStatus } from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";

export type BulkExportAction =
  | "create"
  | "clone"
  | "update"
  | "view"
  | "delete"
  | "submit"
  | "share"
  | "archive"
  | "unarchive"
  | "download"
  | "map";

export function useExportActionAllowed(
  bulkExport: ApiBulkExport | null
): (action: BulkExportAction) => boolean {
  // Returns a function "actionAllowed" that can be used to determine
  // whether current user is allowed to do a given BulkExportAction. The
  // backend checks that too obviously, but this is used for visual effects like
  // disabling buttons.
  const { hasScope } = useContext(LdoAuthContext);

  const status: BulkExportStatus | null = bulkExport ? bulkExport.status : null;

  const isViewer = hasScope("viewer");
  const isAdmin = hasScope("admin");

  // Split off for testing
  return createActionAllowed(status, isViewer, isAdmin);
}

export function createActionAllowed(
  status: BulkExportStatus | null,
  isViewer: boolean,
  isAdmin: boolean
) {
  return function actionAllowed(action: BulkExportAction) {
    const canView =
      // Everybody is allowed to view shared exports.
      status === "shared" ||
      // Complete exports can be viewed by viewers.
      (status === "complete" && (isViewer || isAdmin)) ||
      // Other exports only by admins.
      isAdmin;

    if (action === "view" && canView) {
      return true;
    }

    if (
      action === "map" &&
      (status === "shared" || status === "complete") &&
      (isViewer || isAdmin)
    ) {
      return true;
    }

    // Download block and map should be shown for public exports
    if (action === "download" && status === "shared") return true;

    // Other actions can only be done by admins, and depend on status.
    if (!isAdmin) return false;

    if (action === "create" && status === null) return true;
    if (action === "update" && status === "incomplete") return true;
    if (action === "delete" && status === "incomplete") return true;
    if (action === "submit" && status === "incomplete") return true;
    if (action === "share" && status === "complete") return true;
    if (action === "archive" && (status === "complete" || status === "shared")) return true;
    if (action === "unarchive" && status === "archived") return true;
    if (action === "clone") return true;
    if (action === "download" && canView) return true;

    return false;
  };
}
